/**
 * Hopin shared session authentication reducer and actions
 * Must never be used along with custom domain canvas
 */

import {
  createAsyncThunk,
  createSelector,
  createSlice,
  isRejected,
  Selector,
} from "@reduxjs/toolkit";
import type { TokenResponse } from "src/types";

import { getState } from "@/features/helpers";
import { getIsLoading } from "@/features/requests/requests-reducer";
import { fetchUser } from "@/features/user/user-reducer";
import type { RootState } from "@/redux/root-reducer";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch, fetchJSON } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

export interface SimpleFlowResponse {
  is_simple_flow: boolean;
  reception_url: string;
}

export interface RegistrationResponse {
  registered: boolean;
}

export interface RegistrationReceptionUrlResponse {
  reception_url: string;
}

type AuthenticationState = {
  token: string | null;
  isRegisteredForEvent: boolean | null;
  isSimpleFlow: boolean | null;
  receptionUrl: string | null;
};

const get = endpoints();

const initialState: AuthenticationState = {
  token: null,
  isRegisteredForEvent: null,
  isSimpleFlow: null,
  receptionUrl: null,
};

export const identifyAttendee = createAsyncThunk<
  RegistrationResponse & RegistrationReceptionUrlResponse,
  string
>("user/identifyAttendee", async (slug, { dispatch }) => {
  const response = await dispatch(fetchUserToken());

  if (isRejected(response)) {
    return;
  }
  const registrationStatus = await checkEventRegistrationStatus(slug);
  const registrationReceptionUrl = await checkEventRegistrationReceptionUrl(
    slug
  );

  return { ...registrationStatus, ...registrationReceptionUrl };
});

export const checkEventRegistrationStatus = async (slug: string) => {
  try {
    const response = await authenticatedFetch.fetchJSON<RegistrationResponse>(
      get(`REGISTRATION_STATUS`)(slug),
      {
        method: "POST",
      }
    );

    return isError(response) ? null : response;
  } catch {
    return null;
  }
};

export const checkEventRegistrationReceptionUrl = async (slug: string) => {
  try {
    const response = await authenticatedFetch.fetchJSON<RegistrationReceptionUrlResponse>(
      get("REGISTRATION_RECEPTION_URL")(slug),
      {
        method: "GET",
      }
    );

    return isError(response) ? null : response;
  } catch {
    return null;
  }
};

export const verifySimpleFlow = createAsyncThunk<SimpleFlowResponse, string>(
  "authentication/verifySimpleFlow",
  async (slug) => {
    try {
      const response = await authenticatedFetch.fetchJSON<SimpleFlowResponse>(
        get(`REGISTRATION_SIMPLE_FLOW_VERIFY`)(slug)
      );

      return isError(response) ? null : response;
    } catch {
      return null;
    }
  }
);

export const fetchUserToken = createAsyncThunk<{ token: string }>(
  "authentication/fetchUserToken",
  async (_, { dispatch, rejectWithValue }) => {
    const response = await fetchJSON<TokenResponse>(get("HOPIN_TOKEN"), {
      method: "POST",
      credentials: "include",
    });

    // Monolith /user/sso error response is different from our Pagebuilder error shape.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (response?.error) {
      return rejectWithValue(response);
    }

    authenticatedFetch.prepareHeaders(response as TokenResponse);

    await dispatch(fetchUser());

    return response as { token: string };
  }
);

const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchUserToken.fulfilled, (state, action) => {
      state.token = action.payload.token;
    });
    builder.addCase(identifyAttendee.fulfilled, (state, action) => {
      const { payload } = action;

      state.isRegisteredForEvent = payload?.registered ?? null;
      state.receptionUrl = payload?.reception_url ?? null;
    });
    builder.addCase(verifySimpleFlow.fulfilled, (state, action) => {
      const { payload } = action;
      if (payload) {
        state.isSimpleFlow = payload.is_simple_flow;
      }
    });
  },
});

const getToken: Selector<RootState, string | undefined> = createSelector(
  getState,
  (state) => state.authentication && state.authentication["token"]
);

const getIsAuthenticationLoading = createSelector(getState, (state) =>
  getIsLoading(state, fetchUserToken)
);

const isSimpleFlowSelector = createSelector(
  (state: RootState) => state.authentication.isSimpleFlow,
  (isSimpleFlow) => isSimpleFlow
);

const isUserRegisteredSelector = createSelector(
  (state: RootState) => state.authentication.isRegisteredForEvent,
  (isRegistered) => isRegistered
);

const receptionUrlSelector = createSelector(
  (state: RootState) => state.authentication.receptionUrl,
  (receptionUrl) => receptionUrl
);

export {
  isSimpleFlowSelector,
  isUserRegisteredSelector,
  receptionUrlSelector,
  getToken,
  getIsAuthenticationLoading,
};

export default authenticationSlice.reducer;
